// /Users/donovan68/dva-frontend-vite/src/pages/pro/ApprovalPage.tsx
// Step 3: Approval & Execution - Strategic Approval workflow
// UPDATED: New layout - Header above journey bar, breadcrumbs removed

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Users, Clock, Shield, Briefcase, ScrollText, BookOpen } from "lucide-react";
import { ProProgressPathV3 } from "../../components/ProProgressPathV3";
import ProJourneyPageHeader from "../../components/journey/pro/ProJourneyPageHeader";
import ProspectPanel from "../../components/ProspectPanel";
import { Step3TabNav } from "../../components/Step3TabNav";
import { MessagesContent } from "../../components/MessagesContent";
import { FreemiumCard } from "../../components/FreemiumCard";
import { useFilter } from "../../contexts/FilterContext";
import { usePageContext } from "../../contexts/PageContext";
import { useClient } from "../../contexts/ClientContext";
import { useProspectList } from "../../hooks/useProspectList";
import { convertToProspectPanelFormat } from "../../utils/prospectUtils";

export default function ApprovalPage() {
  const navigate = useNavigate();
  const { activeFilter, setActiveFilter } = useFilter();
  const { setPageContext } = usePageContext();
  const { clientId } = useClient();
  const { prospects: rawProspects, updateStatus } = useProspectList(clientId || '');
  const prospects = convertToProspectPanelFormat(rawProspects);
  const messageCount = 4;

  // Handlers for ProspectPanel
  const handleProspectRemove = async (prospectId: string) => {
    if (!clientId) return;
    try {
      await updateStatus(Number(prospectId), 'qualified');
    } catch (err) {
      console.error('Failed to remove prospect:', err);
    }
  };

  const handleProfileClick = (prospectId: string) => {
    navigate(`/usecase/${prospectId}`);
  };

  const handleRoiClick = (prospectId: string) => {
    navigate(`/pro/analysis/${prospectId}`);
  };

  const handlePlanClick = (prospectId: string) => {
    navigate(`/pro/roadmap-planning`);
  };

  const handleReorder = (reordered: any[]) => {
    console.log('Prospects reordered:', reordered);
  };

  // Set page context - REMOVED breadcrumbs
  useEffect(() => {
    setPageContext({
      breadcrumbs: [],
      tabs: ["Tools", "Documents", "Messages"]
    });
  }, [setPageContext]);

  // TODO: Replace with real data from hooks/services
  const personalizationData = {
    strategy: 100,
    capabilities: 95,
    readiness: 85,
    docsCount: 7
  };

  // Handle phase navigation (updated for 3-step system)
  const handlePhaseClick = (phaseId: string) => {
    switch(phaseId) {
      case 'portfolio':
        navigate('/home');
        break;
      case 'analysis':
        navigate('/pro/analysis');
        break;
      case 'approval_execution':
        navigate('/pro/approval'); // Stay on this page
        break;
      default:
        navigate('/home');
    }
  };

  // DOCUMENTS CONTENT (3 cards for Approval)
  const documentsCards = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Governance Framework Guide",
      description: "Establish effective data governance and approval processes.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "Committee charter templates",
        "Decision-making frameworks",
        "Authority matrix examples",
        "Policy approval workflows"
      ],
      isPremium: false,
      fileType: "PDF",
      fileSize: "4.1 MB"
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Executive Presentation Templates",
      description: "Stakeholder-ready materials for securing buy-in and funding.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "Board presentation decks",
        "Executive summary templates",
        "Budget justification guides",
        "Risk assessment frameworks"
      ],
      isPremium: false,
      fileType: "PPTX",
      fileSize: "6.3 MB"
    },
    {
      icon: <ScrollText className="w-6 h-6" />,
      title: "Business Case Builder",
      description: "Tools for crafting compelling business cases for analytics initiatives.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "Business case templates",
        "Financial justification guides",
        "Stakeholder analysis tools",
        "Approval tracking checklists"
      ],
      isPremium: false,
      fileType: "DOCX",
      fileSize: "2.9 MB"
    },
  ];

  const stages = [
    { name: "Intake", items: [
    { id: "UC-001", title: "Clinical Analytics", owner: "Dr. Smith", status: "review", updated: "2d ago" },
    { id: "UC-002", title: "Pop Health", owner: "J. Doe", status: "pending", updated: "5d ago" },
    ]},
    { name: "Review", items: [
    { id: "UC-003", title: "Predictive Models", owner: "A. Johnson", status: "in-review", updated: "1d ago" },
    ]},
    { name: "Finance", items: [
    { id: "UC-004", title: "Interoperability", owner: "M. Lee", status: "approved", updated: "3h ago" },
    ]},
    { name: "Board", items: [
    { id: "UC-005", title: "Quality Measures", owner: "S. Chen", status: "scheduled", updated: "1w ago" },
    ]},
  ];

  return (
    <main className="max-w-[1440px] mx-auto px-6 pb-4">
      {/* NEW LAYOUT ORDER: Tabs → Page Header → Journey Bar → Step3 Tabs → Content */}
      
      {/* 0. Content Tabs (Tools / Documents / Messages) - Same position as header tabs */}
      <div className="h-10 flex items-center justify-end gap-2 pt-3 mb-0.5">
        <nav className="flex items-center gap-2" aria-label="Section navigation">
          {['Tools', 'Documents', 'Messages'].map((tab) => {
            const active = tab === activeFilter;
            const isMessages = tab === 'Messages';
            const showBadge = isMessages && messageCount > 0;
            
            return (
              <button
                key={tab}
                onClick={() => setActiveFilter(tab)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors relative
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#18365E]
                  ${active ? "bg-[#18365E] text-white" : "text-[#18365E] hover:bg-black/5"}`}
                aria-current={active ? "page" : undefined}
              >
                {tab}
                {showBadge && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#FF6E4C] text-[10px] font-bold text-white">
                    {messageCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>
      
      {/* 1. Page Header */}
      <div className="mb-6 -mt-2">
        <ProJourneyPageHeader
          stepNumber={3}
          stepName="Approval & Execution"
          description="Secure strategic approval through governance workflow and transition initiatives to execution."
        />
      </div>

      {/* 2. Journey Bar */}
      <div className="mb-6">
        <ProProgressPathV3 
          currentPhase="approval_execution"
          personalizationData={personalizationData}
          onPersonalizationClick={() => navigate("/personalize")}
          onPhaseClick={handlePhaseClick}
        />
      </div>

      {/* 3. Step 3 Tab Navigation (Roadmap / Approval / Execution) */}
      <div className="mb-6">
        <Step3TabNav />
      </div>

      {/* 4. Main content with fixed right panel */}
      <div className="flex gap-0">
        {/* LEFT: main content */}
        <div className="flex-1 min-w-0 space-y-6">

          {/* Conditional content based on activeFilter */}
      {activeFilter === 'Messages' ? (
        <MessagesContent />
      ) : activeFilter === 'Documents' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documentsCards.map((card, idx) => (
            <FreemiumCard
              key={idx}
              icon={card.icon}
              title={card.title}
              description={card.description}
              contentType={card.contentType}
              listItems={card.listItems}
              isPremium={card.isPremium}
            />
          ))}
        </div>
      ) : (
        /* Tools view - Approval pipeline (default) */
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {stages.map((stage) => (
            <div key={stage.name} className="bg-white rounded-xl ring-1 ring-[#e5e7eb] p-4 shadow-sm">
              <h3 className="text-sm font-bold text-[#18365E] mb-3">{stage.name}</h3>
              <div className="space-y-2">
                {stage.items.map((item) => {
                  // Extract use case ID from item.id if it follows UC-XXX pattern
                  // For mock data, we'll use a placeholder ID; in real implementation, use actual use_case_id
                  const useCaseId = item.id?.match(/UC-(\d+)/)?.[1] || null;
                  
                  return (
                    <div key={item.id} className="p-3 rounded-lg bg-[#F5F7FA] hover:bg-[#e5e7eb] transition-colors">
                      <div className="text-xs font-bold text-[#18365E] mb-1">{item.id}</div>
                      <div className="text-sm font-semibold text-[#18365E] mb-2">{item.title}</div>
                      <div className="flex items-center gap-2 text-xs text-[#717182]">
                        <Users className="w-3 h-3" />
                        <span>{item.owner}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-[#717182] mt-1">
                        <Clock className="w-3 h-3" />
                        <span>{item.updated}</span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-slate-200">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(useCaseId ? `/pro/resources?useCaseId=${useCaseId}` : '/pro/resources');
                          }}
                          className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1 transition-colors"
                        >
                          <BookOpen className="w-3 h-3" />
                          Resources
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        </>
          )}
        </div>

        {/* RIGHT: Prospect Panel - Fixed */}
        {clientId && (
          <div className="hidden lg:block">
            <ProspectPanel
              prospects={prospects}
              onRemove={handleProspectRemove}
              onProfileClick={handleProfileClick}
              onRoiClick={handleRoiClick}
              onPlanClick={handlePlanClick}
              onReorder={handleReorder}
            />
          </div>
        )}
      </div>
    </main>
  );
}